import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown, Send, CheckCircle2, FileText, PlayCircle, Users, Phone, ExternalLink } from 'lucide-react';
import { navigation, features, services, contactInfo } from './data';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GammelSideApp from './pages/GammelSide';
import MedimiSmartTim from './pages/MedimiSmartTim';
import FlexSmartApp from './pages/FlexSmartApp';
import Mobili from './pages/Mobili';
import Instruktioner from './pages/Instruktioner';
import ELaering from './pages/ELaering';
import VideoWatch from './pages/VideoWatch';
import VideoLibrary from './components/VideoLibrary';
import { allVideos, getVideoUrl } from './data/videoData';
import useDocumentMetadata from './hooks/useDocumentMetadata';
import Tim3DViewer from './components/Tim3DViewer';
import FolderFlipbook from './components/FolderFlipbook';

const Header = ({ isMenuOpen, setIsMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const scrollToSection = (targetId) => {
    const element = document.getElementById(targetId);
    if (element) {
      const header = document.querySelector('header');
      const headerHeight = header ? header.offsetHeight : 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  const handleNavClick = (href, e) => {
    if (e) e.preventDefault();
    setIsMenuOpen(false);
    
    const targetId = href.includes('#') ? href.split('#')[1] : null;
    
    if (targetId) {
      if (location.pathname === '/') {
        scrollToSection(targetId);
      } else {
        navigate('/');
        // Wait for navigation to complete before scrolling
        setTimeout(() => {
          scrollToSection(targetId);
        }, 150);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <div className="bg-slate-900 text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-[10px] uppercase tracking-widest font-bold">
          <div className="flex gap-6">
            <span className="flex items-center gap-1">Kontakt: +45 40 83 08 30</span>
          </div>
          <a href="mailto:info@mediccare.dk" className="hover:text-brand-light transition-colors">E-mail: info@mediccare.dk</a>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-[#323926] border-b border-white/10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 xl:px-8">
          <div className="flex justify-between items-center py-4 lg:py-4 xl:py-5">
            <div className="flex items-center">
              <Link to="/">
                <img src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mediccare-v3-mork.jpg" alt="MedicCare" className="h-8 md:h-9 lg:h-9 xl:h-11 w-auto transition-all flex-shrink-0" />
              </Link>
            </div>

            <nav className="hidden lg:flex items-center gap-3 lg:gap-3.5 xl:gap-7 2xl:gap-10">
              {navigation.map((item) => (
                item.children ? (
                  <div key={item.name} className="relative group py-2">
                    <Link to={item.href} className="flex items-center gap-1 text-[11px] xl:text-xs font-bold uppercase tracking-wider xl:tracking-widest whitespace-nowrap text-white/80 hover:text-white transition-colors cursor-pointer">
                      {item.name} <ChevronDown size={14} className="transition-transform group-hover:rotate-180 flex-shrink-0" />
                    </Link>
                    <div className="absolute left-0 mt-2 w-56 xl:w-64 bg-white rounded-lg shadow-xl py-2 border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      {item.children.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 hover:text-[#2c3223] transition-colors whitespace-nowrap"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : item.href.startsWith('/#') || item.href === '/' ? (
                  <a key={item.name} href={item.href} onClick={(e) => handleNavClick(item.href, e)} className="text-[11px] xl:text-xs font-bold uppercase tracking-wider xl:tracking-widest whitespace-nowrap text-white/80 hover:text-white transition-colors">
                    {item.name}
                  </a>
                ) : item.target === '_blank' || item.name === 'E-læring' ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] xl:text-xs font-bold uppercase tracking-wider xl:tracking-widest whitespace-nowrap text-white/80 hover:text-white transition-colors"
                  >
                    {item.name}
                    <ExternalLink size={12} className="opacity-70 flex-shrink-0" />
                  </a>
                ) : (
                  <Link key={item.name} to={item.href} className="text-[11px] xl:text-xs font-bold uppercase tracking-wider xl:tracking-widest whitespace-nowrap text-white/80 hover:text-white transition-colors">
                    {item.name}
                  </Link>
                )
              ))}
              <a href="/#kontakt" onClick={(e) => handleNavClick('/#kontakt', e)} className="bg-white text-[#2c3223] text-[10px] font-bold uppercase tracking-wider xl:tracking-widest px-4 lg:px-4.5 xl:px-8 py-2 lg:py-2.5 xl:py-3 rounded-full hover:bg-brand-soft transition-all transform hover:-translate-y-0.5 whitespace-nowrap flex-shrink-0">
                Kontakt
              </a>
            </nav>

            <button className="lg:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-[#323926] border-b border-white/10 px-4 py-6 space-y-4">
            {navigation.map((item) => (
              <div key={item.name} className="space-y-2">
                {item.children ? (
                  <>
                    <Link
                      to={item.href}
                      className="block text-sm font-bold uppercase tracking-widest text-white hover:text-brand-light"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                    <div className="pl-4 space-y-2 border-l border-white/10 mt-2">
                      {item.children.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block text-xs font-bold uppercase tracking-widest text-white/80 hover:text-white"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : item.href.startsWith('/#') || item.href === '/' ? (
                  <a href={item.href} className="block text-sm font-bold uppercase tracking-widest text-white/80" onClick={(e) => handleNavClick(item.href, e)}>
                    {item.name}
                  </a>
                ) : item.target === '_blank' || item.name === 'E-læring' ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-sm font-bold uppercase tracking-widest text-white/80 hover:text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{item.name}</span>
                    <ExternalLink size={14} className="opacity-70" />
                  </a>
                ) : (
                  <Link to={item.href} className="block text-sm font-bold uppercase tracking-widest text-white/80" onClick={() => setIsMenuOpen(false)}>
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <a href="/#kontakt" className="block text-center bg-white text-[#2c3223] text-xs font-bold uppercase tracking-widest py-4 rounded-full" onClick={(e) => handleNavClick('/#kontakt', e)}>
              Kontakt
            </a>
          </div>
        )}
      </header>
    </>
  );
};

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleFooterNavClick = (href, e) => {
    if (e) e.preventDefault();
    const targetId = href.includes('#') ? href.split('#')[1] : null;
    if (targetId) {
      const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 90;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerHeight;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      };

      if (location.pathname === '/') {
        scrollToSection(targetId);
      } else {
        navigate('/');
        setTimeout(() => {
          scrollToSection(targetId);
        }, 150);
      }
    }
  };

  return (
    <footer className="relative bg-[#323926] text-white pt-12 pb-6 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src="/mediccare_hero_bg.jpg" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#323926] via-[#323926]/80 to-[#323926]/40 backdrop-blur-[2px]"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center md:text-left relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-12 mb-10">
          <div className="space-y-6 flex flex-col items-center md:items-start">
            <img src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mediccare-v3-mork.jpg" alt="MedicCare" className="h-8 md:h-10 lg:h-11 w-auto transition-all" />
            <div className="text-xs text-slate-300 space-y-2 text-center md:text-left">
              <p className="font-bold text-white text-sm">MedicCare A/S</p>
              <p>Jyllandsgade 30 1</p>
              <p>6400 Sønderborg, Danmark</p>
              <div className="pt-3 flex justify-center md:justify-start">
                <a
                  href="/#kontakt"
                  onClick={(e) => handleFooterNavClick('/#kontakt', e)}
                  className="inline-flex items-center justify-center bg-white text-[#2c3223] text-xs font-bold uppercase tracking-widest px-6 py-2.5 rounded-full hover:bg-brand-soft transition-all transform hover:-translate-y-0.5 shadow-sm cursor-pointer"
                >
                  Kontakt
                </a>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:flex gap-x-16 gap-y-8 justify-center w-full md:w-auto text-center md:text-left">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white mb-4">Navigering</h4>
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">Forside</Link>
              <Link to="/folder" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">Folder</Link>
              <Link to="/video" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">Video</Link>
              <Link to="/om-os" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">Om os</Link>
            </div>
          </div>
          <div>
            <Link to="/medimi-smart">
              <h4 className="text-xs font-bold uppercase tracking-widest text-white hover:text-slate-300 transition-colors mb-4">Produkter</h4>
            </Link>
            <div className="flex flex-col gap-3">
              <Link to="/medimi-smart" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">FlexSuite</Link>
              <Link to="/medimi-smart/tim" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">TIM</Link>
              <Link to="/medimi-smart/flexsmartapp" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">APP</Link>
              <Link to="/medimi-smart/mobili" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">Mobili</Link>
              <Link to="/instruktioner" className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors">Instrukser</Link>
              <a
                href="/e-laering"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors"
              >
                E-læring <ExternalLink size={10} className="opacity-70" />
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] text-slate-400 uppercase tracking-[0.2em] font-medium">
        <p>© 2026 MedicCare. Alle rettigheder forbeholdes.</p>
        <div className="flex gap-8">
          <Link to="/privatlivspolitik" className="hover:text-white transition-colors">Privatlivspolitik</Link>
          <a href="#" className="hover:text-white transition-colors">Handelsbetingelser</a>
        </div>
      </div>
    </div>
  </footer>
  );
};

const useScrollReveal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return [domRef, isVisible];
};

const RevealSection = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-1000 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
    >
      {children}
    </div>
  );
};

const InteractiveFlow = () => {
  const [activeNode, setActiveNode] = useState(null);
  const [scale, setScale] = useState(1);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.getBoundingClientRect().width;
        if (parentWidth < 1300) {
          setScale(parentWidth / 1300);
        } else {
          setScale(1);
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(handleResize, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const nodes = [
    { id: 'laege', title: 'Lægen – ordinationen', label: 'Læge', text: 'Lægen ordinerer medicinen i FMK (Fælles Medicin Kort). Herfra overføres oplysningerne automatisk til kommunens omsorgssystem, så både kommune og apotek arbejder ud fra de samme, opdaterede data.', className: 'node-laege', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/chatgpt-image-2-jul-2026-08-53-35.png', tooltipClass: 'tooltip-bottom-left' },
    { id: 'fmk', title: 'FMK', label: 'FMK', text: 'FMK (Fælles Medicin Kort) er den digitale platform, hvor lægen lægger medicinordinationen. Det er herfra, kommunen og apoteket trækker oplysninger om borgerens medicin.', className: 'node-fmk', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/papir.png', tooltipClass: 'tooltip-bottom-center' },
    { id: 'eoj', title: 'EOJ – kommunens omsorgssystem', label: 'EOJ', text: 'EOJ-systemet (f.eks. NEXUS eller CURA) modtager medicinoplysninger fra FMK og fungerer som kommunens centrale platform for dokumentation og koordinering. Herfra sendes relevante data videre til FlexSuite.', className: 'node-eoj', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/eoj.png', tooltipClass: 'tooltip-bottom-center' },
    { id: 'flex', title: 'FlexSuite – bindeleddet', label: 'FlexSuite', text: 'FlexSuite forbinder omsorgssystemet, apoteket og TIM-robotten. Systemet sikrer, at ændringer i medicin, leverancer og statusoplysninger automatisk udveksles mellem alle parter.', className: 'node-flex', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsuite.png', tooltipClass: 'tooltip-bottom-center' },
    { id: 'tim', title: 'TIM – udlevering og påmindelse', label: 'TIM', text: 'TIM står for den daglige dosering og påmindelse. På rette tidspunkt påminder TIM-robotten borgeren om at trykke på knappen for medicindosering. Robotten registrerer, om borgeren tager glasset med de doserede piller. Det reducerer fejl og sikrer en stabil og dokumenteret medicinadministration.', className: 'house', isHouse: true, tooltipClass: 'tooltip-left-center' },
    { id: 'apotek', title: 'Apoteket – ompakning, kvalitetssikring og levering', label: 'Apotek', text: 'Apoteket modtager medicinoplysningerne og pakker medicinen i kassetter, der passer til TIM-robotten. Apoteket giver automatisk besked til kommunen, hvis der mangler recepter eller opstår afvigelser.', className: 'node-apotek', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medidcin.png', tooltipClass: 'tooltip-top-center' },
    { id: 'leverance', title: 'Medicinleverance', label: 'Medicinleverance', text: 'Apoteket eller eksterne aktører henter medicinen på apoteket og kører til borgeren og sætter medicinkassetterne ind i TIM.', className: 'node-leverance', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/bil.png', tooltipClass: 'tooltip-top-center' },
    { id: 'lager', title: 'Lager', label: 'Lager', text: 'Lager af TIM-robotter, hvorfra de klargøres inden opsætning hos borgerne.', className: 'node-lager', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/lager.png', tooltipClass: 'tooltip-top-center' },
    { id: 'support', title: 'Sundhedssupport', label: 'Sundhedssupport', text: 'TIM-robotten overvåges af Sundhedssupporten, som kan kontaktes ved spørgsmål, fejl eller særlige behov – fx ferieplanlægning.', className: 'node-support', image: 'https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/support.png', tooltipClass: 'tooltip-top-right' },
  ];

  const activeInfo = nodes.find(n => n.id === activeNode);

  return (
    <div className="w-full overflow-hidden py-24 bg-slate-50 border-t border-slate-100" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight leading-tight">
            Konsortiet MedicCare Flow
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            MedicCare leverer digitale løsninger til medicinhåndtering ude i borgers eget hjem.
            <br className="hidden md:inline" /> Vi integrerer mellem kommunernes systemer og vores intelligente medicinrobotter, hvilket giver en helhedsløsning fra lægens ordination til borgerens hånd.
          </p>
        </div>

        {/* Desktop/Tablet interaktivt flow */}
        <div className="hidden lg:block">
          {/* Scaled wrapper to prevent horizontal scroll */}
          <div 
            className="relative mx-auto my-12 transition-all duration-300 ease-out"
            style={{
              width: '1300px',
              height: `${720 * scale}px`,
            }}
          >
            <div 
              className="absolute top-0 left-0 origin-top-left transition-transform duration-300 ease-out"
              style={{
                transform: `scale(${scale})`,
                width: '1300px',
                height: '720px',
              }}
            >
              <div className="interactive-flow-map relative w-[1300px] h-[720px]">
                {/* Lines */}
                <div className="line arrow-double laege-fmk-line"></div>
                <div className="line arrow-double fmk-eoj-line"></div>
                <div className="line arrow-double eoj-flex-line"></div>
                <div className="line arrow-double flex-tim-line"></div>
                <div className="line arrow-double fmk-apotek-line"></div>
                <div className="line arrow-double flex-vertical-line"></div>
                <div className="line arrow-double apotek-leverance-line"></div>
                <div className="line arrow-double leverance-lager-line"></div>
                <div className="line arrow-double lager-support-line"></div>
                <div className="line arrow-double support-tim-line"></div>

                
                

                {/* Nodes */}
                {nodes.map((node) => {
                  const isHovered = activeNode === node.id;
                  if (node.isHouse) {
                    return (
                      <div 
                        key={node.id}
                        className={`house node cursor-pointer ${isHovered ? 'hovered' : ''}`}
                        onMouseEnter={() => setActiveNode(node.id)}
                        onMouseLeave={() => setActiveNode(null)}
                        onClick={() => setActiveNode(node.id)}
                      >
                        <div className="house-bg"></div>
                        <img 
                          src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/borger.png" 
                          alt="Borger" 
                          className="w-16 h-16 object-contain mx-auto mb-2 mt-1"
                        />
                        <p className="borger-text font-bold text-slate-800">Borger</p>

                        <div className="tim-circle">
                          <img 
                            src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png" 
                            alt="TIM" 
                            className="w-20 h-20 object-contain" 
                          />
                        </div>
                        <p className="tim-text font-bold text-slate-800">TIM</p>

                        <img 
                          src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/tlf.png" 
                          alt="Mobiltelefon" 
                          className="w-11 h-16 object-contain absolute left-[75px] top-[160px]" 
                        />
                        <img 
                          src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/cirkel.png" 
                          alt="Info enhed" 
                          className="w-12 h-12 object-contain absolute right-[70px] top-[165px]" 
                        />

                        {/* Tooltip */}
                        {isHovered && scale >= 0.7 && (
                          <div className={`tooltip-box ${node.tooltipClass}`}>
                            <h3>{node.title}</h3>
                            <p>{node.text}</p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div 
                      key={node.id}
                      className={`node ${node.className} cursor-pointer ${isHovered ? 'hovered' : ''}`}
                      onMouseEnter={() => setActiveNode(node.id)}
                      onMouseLeave={() => setActiveNode(null)}
                      onClick={() => setActiveNode(node.id)}
                    >
                      <div className="circle">
                        {node.image ? (
                          <img src={node.image} alt={node.title} className="w-20 h-20 object-contain" />
                        ) : (
                          <i className={`${node.icon} text-5xl text-[#003f4c]`}></i>
                        )}
                      </div>
                      <button className="hotspot" aria-label={`Vis information om ${node.title}`}></button>
                      <p className="font-bold text-slate-800">{node.label || node.title}</p>

                      {/* Tooltip */}
                      {isHovered && scale >= 0.7 && (
                        <div className={`tooltip-box ${node.tooltipClass}`}>
                          <h3>{node.title}</h3>
                          <p>{node.text}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Fallback Info Box for mobile so text is not scaled down to unreadability */}
          {scale < 0.7 && activeInfo && (
            <div className="mt-6 bg-white rounded-xl p-6 shadow-lg border-l-4 border-[#00b7ad] animate-fadeIn">
              <h3 className="text-xl font-bold text-[#003f4c] mb-2">{activeInfo.title}</h3>
              <p className="text-slate-600 text-base leading-relaxed">{activeInfo.text}</p>
            </div>
          )}
        </div>

        {/* Mobilvisning (vises kun på mindre skærme) */}
        <div className="block lg:hidden space-y-8">
          {/* Flowdiagram Billede */}
          <div className="bg-white p-4 rounded-2xl shadow-lg border border-slate-100">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mediccare-flow_3.jpg" 
              alt="MedicCare Flow Diagram" 
              className="w-full h-auto rounded-xl"
            />
          </div>

          {/* Beskrivende kasser */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nodes.map((node, index) => {
              const nodeImage = node.image || (node.id === 'tim' ? 'https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png' : null);
              return (
                <div 
                  key={node.id} 
                  className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex gap-4 items-start transition-all duration-300 hover:shadow-md hover:border-[#00b7ad]/30"
                >
                  <div className="w-14 h-14 rounded-full bg-[#c7e4e7]/60 flex items-center justify-center shrink-0 border border-[#00b7ad]/10">
                    {nodeImage ? (
                      <img src={nodeImage} alt={node.title} className="w-9 h-9 object-contain" />
                    ) : (
                      <i className={`${node.icon} text-2xl text-[#003f4c]`}></i>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-[#003f4c]">{node.title}</h3>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">{node.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .interactive-flow-map {
          --teal: #00b7ad;
          --dark: #003f4c;
          --light: #c7e4e7;
          --sprite: url('https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/chatgpt-image-2-jul-2026-08-53-35.png');
          font-family: system-ui, -apple-system, sans-serif;
        }

        .interactive-flow-map .node {
          position: absolute;
          width: 150px;
          text-align: center;
          z-index: 5;
        }

        .interactive-flow-map .node.hovered,
        .interactive-flow-map .house.hovered {
          z-index: 100;
        }

        .interactive-flow-map .circle {
          width: 140px;
          height: 140px;
          background: var(--light);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: auto;
          transition: all 0.3s ease;
        }

        .interactive-flow-map .node p,
        .interactive-flow-map .house p {
          font-size: 20px;
          margin: 24px 0 0;
          font-weight: 400;
          letter-spacing: 0.02em;
        }

        .interactive-flow-map .sprite {
          display: block;
          background-image: var(--sprite);
          background-repeat: no-repeat;
          background-size: 1300px auto;
        }

        /* icon crops */
        .interactive-flow-map .laege {
          width: 95px;
          height: 82px;
          background-position: -75px -190px;
        }

        .interactive-flow-map .fmk {
          width: 105px;
          height: 82px;
          background-position: -285px -190px;
        }

        .interactive-flow-map .eoj {
          width: 100px;
          height: 82px;
          background-position: -495px -190px;
        }

        .interactive-flow-map .flex {
          width: 110px;
          height: 90px;
          background-position: -705px -185px;
        }

        .interactive-flow-map .apotek {
          width: 110px;
          height: 90px;
          background-position: -355px -540px;
        }

        .interactive-flow-map .leverance {
          width: 110px;
          height: 82px;
          background-position: -570px -550px;
        }

        .interactive-flow-map .lager {
          width: 105px;
          height: 95px;
          background-position: -790px -535px;
        }

        .interactive-flow-map .support {
          width: 110px;
          height: 95px;
          background-position: -1015px -535px;
        }

        .interactive-flow-map .borger {
          width: 80px;
          height: 75px;
          margin: 0 auto;
          background-position: -1070px -55px;
        }

        .interactive-flow-map .tim {
          width: 105px;
          height: 105px;
          background-position: -1050px -190px;
        }

        .interactive-flow-map .phone {
          position: absolute;
          left: 75px;
          top: 160px;
          width: 45px;
          height: 70px;
          background-position: -980px -220px;
        }

        .interactive-flow-map .device {
          position: absolute;
          right: 70px;
          top: 165px;
          width: 55px;
          height: 65px;
          background-position: -1190px -225px;
        }

        /* positions */
        .interactive-flow-map .node-laege { left: 20px; top: 120px; }
        .interactive-flow-map .node-fmk { left: 220px; top: 120px; }
        .interactive-flow-map .node-eoj { left: 430px; top: 120px; }
        .interactive-flow-map .node-flex { left: 650px; top: 120px; }

        .interactive-flow-map .node-apotek { left: 220px; top: 470px; }
        .interactive-flow-map .node-leverance { left: 430px; top: 470px; }
        .interactive-flow-map .node-lager { left: 650px; top: 470px; }
        .interactive-flow-map .node-support { left: 870px; top: 470px; }

        .interactive-flow-map .house {
          position: absolute;
          right: 0;
          top: 20px;
          width: 360px;
          height: 380px;
          text-align: center;
          z-index: 5;
          padding-top: 55px;
          transition: all 0.3s ease;
        }

        .interactive-flow-map .house-bg {
          position: absolute;
          inset: 0;
          background: var(--light);
          clip-path: polygon(50% 0%, 100% 36%, 90% 36%, 90% 100%, 10% 100%, 10% 36%, 0 36%);
          z-index: -1;
          transition: all 0.3s ease;
        }

        .interactive-flow-map .borger-text {
          margin-top: 5px !important;
        }

        .interactive-flow-map .house .tim-text {
          margin-top: 8px !important;
        }

        .interactive-flow-map .tim-circle {
          width: 120px;
          height: 120px;
          background: #fff;
          border-radius: 50%;
          margin: 15px auto 0;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .interactive-flow-map .hotspot {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 4px solid var(--dark);
          background: #fff;
          position: absolute;
          left: 50%;
          top: 120px;
          transform: translateX(-50%);
          cursor: pointer;
          z-index: 20;
          transition: all 0.3s ease;
        }

        .interactive-flow-map .house .hotspot {
          top: 250px;
        }

        .interactive-flow-map .node:hover .hotspot,
        .interactive-flow-map .house:hover .hotspot,
        .interactive-flow-map .node.hovered .hotspot,
        .interactive-flow-map .house.hovered .hotspot {
          transform: translateX(-50%) scale(1.18);
          background: var(--teal);
          box-shadow: 0 0 0 10px rgba(0,183,173,.18);
        }

        .interactive-flow-map .node:hover .circle,
        .interactive-flow-map .house:hover .tim-circle,
        .interactive-flow-map .node.hovered .circle,
        .interactive-flow-map .house.hovered .tim-circle {
          transform: translateY(-6px);
          box-shadow: 0 0 0 8px rgba(0,183,173,.18);
        }

        /* lines */
        .interactive-flow-map .line {
          position: absolute;
          height: 5px;
          background: var(--teal);
          z-index: 6;
          transition: all 0.3s ease;
          opacity: 0.75;
        }

        .interactive-flow-map .laege-fmk-line {
          left: 117px;
          top: 260px;
          width: 156px;
        }

        .interactive-flow-map .fmk-eoj-line {
          left: 317px;
          top: 260px;
          width: 166px;
        }

        .interactive-flow-map .eoj-flex-line {
          left: 527px;
          top: 260px;
          width: 176px;
        }

        .interactive-flow-map .flex-tim-line {
          left: 747px;
          top: 260px;
          width: 229px;
        }

        .interactive-flow-map .fmk-apotek-line {
          left: 295px;
          top: 315px;
          width: 155px;
          transform-origin: top left;
          transform: rotate(90deg);
        }

        .interactive-flow-map .flex-vertical-line {
          left: 725px;
          top: 315px;
          width: 155px;
          transform-origin: top left;
          transform: rotate(90deg);
        }

        .interactive-flow-map .apotek-leverance-line {
          left: 295px;
          top: 470px;
          width: 210px;
        }

        .interactive-flow-map .leverance-lager-line {
          left: 505px;
          top: 470px;
          width: 220px;
        }

        .interactive-flow-map .lager-support-line {
          left: 725px;
          top: 470px;
          width: 220px;
        }

        .interactive-flow-map .support-tim-line {
          left: 945px;
          top: 470px;
          width: 78px;
          transform-origin: top left;
          transform: rotate(-66.1deg);
        }

        /* Arrowheads base */
        .interactive-flow-map .line::before,
        .interactive-flow-map .line::after {
          content: "";
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          border-top: 3px solid transparent;
          border-bottom: 3px solid transparent;
          opacity: 0;
          transition: all 0.3s ease;
        }

        /* Left / Start Arrow */
        .interactive-flow-map .line.arrow-left::before,
        .interactive-flow-map .line.arrow-double::before {
          left: -1px;
          border-right: 2.5px solid var(--teal);
          opacity: 1;
        }

        /* Right / End Arrow */
        .interactive-flow-map .line.arrow-right::after,
        .interactive-flow-map .line.arrow-double::after {
          right: -1px;
          border-left: 2.5px solid var(--teal);
          opacity: 1;
        }

        /* line active states removed */

        /* tooltip box */
        .interactive-flow-map .tooltip-box {
          position: absolute;
          width: 300px;
          background: #ffffff;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.15);
          border-left: 4px solid var(--teal);
          z-index: 100;
          text-align: left;
          pointer-events: none;
          animation: tooltipSnapIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
        }

        @keyframes tooltipSnapIn {
          0% {
            opacity: 0;
            transform: var(--tooltip-translate, translateX(0)) scale(0.85);
          }
          100% {
            opacity: 1;
            transform: var(--tooltip-translate, translateX(0)) scale(1);
          }
        }

        .interactive-flow-map .tooltip-box h3 {
          margin: 0 0 6px;
          color: var(--dark);
          font-size: 18px;
          font-weight: bold;
        }

        .interactive-flow-map .tooltip-box p {
          margin: 0;
          font-size: 14px;
          line-height: 1.4;
          color: #475569;
        }

        /* Tooltip positions and arrows */
        .interactive-flow-map .tooltip-bottom-center {
          --tooltip-translate: translateX(-50%);
          top: 195px;
          left: 50%;
          transform: var(--tooltip-translate);
        }
        .interactive-flow-map .tooltip-bottom-center::before {
          content: "";
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid #ffffff;
        }

        .interactive-flow-map .tooltip-bottom-left {
          --tooltip-translate: translateX(0);
          top: 195px;
          left: 0px;
          transform: var(--tooltip-translate);
        }
        .interactive-flow-map .tooltip-bottom-left::before {
          content: "";
          position: absolute;
          top: -8px;
          left: 75px;
          transform: translateX(-50%);
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid #ffffff;
        }

        .interactive-flow-map .tooltip-top-center {
          --tooltip-translate: translateX(-50%) translateY(-15px);
          bottom: 100%;
          left: 50%;
          transform: var(--tooltip-translate);
        }
        .interactive-flow-map .tooltip-top-center::after {
          content: "";
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid #ffffff;
        }

        .interactive-flow-map .tooltip-top-right {
          --tooltip-translate: translateY(-15px);
          bottom: 100%;
          right: 0px;
          transform: var(--tooltip-translate);
        }
        .interactive-flow-map .tooltip-top-right::after {
          content: "";
          position: absolute;
          bottom: -8px;
          right: 75px;
          transform: translateX(50%);
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid #ffffff;
        }

        .interactive-flow-map .tooltip-left-center {
          --tooltip-translate: translateY(0);
          top: 100px;
          left: -320px;
          transform: var(--tooltip-translate);
        }
        .interactive-flow-map .tooltip-left-center::after {
          content: "";
          position: absolute;
          right: -8px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 8px solid transparent;
          border-bottom: 8px solid transparent;
          border-left: 8px solid #ffffff;
        }
      `}</style>
    </div>
  );
};

const LandingPage = () => {
  useDocumentMetadata({
    title: 'MedicCare | Intelligent medicinhåndtering og tryghed i hverdagen',
    description: 'MedicCare leverer intelligente medicindispensers og velfærdsteknologi, der sikrer korrekt medicinering til tiden og skaber tryghed for borgere, pårørende og plejeperonale.'
  });

  const [formStatus, setFormStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) setFormStatus('success');
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 py-20 lg:py-32">
        <div className="absolute inset-0 z-0">
           <img src="/shutterstock_2684544319.jpg" alt="" className="w-full h-full object-cover opacity-60" />
           <div className="absolute inset-0 bg-[#2c3223]/80 mix-blend-multiply"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col items-start text-left">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6">
                Fremtidens digitale <span className="text-brand-light">medicinhåndtering</span>
              </h1>
              <p className="text-lg text-slate-200 mb-10">
                Vi har løsninger til medicinhåndtering i omsorgs-, social og sundhedsområdet i de danske kommuner. ”Helhedsløsning eller tilpasset kommunens behov”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Diagram Section */}
      <InteractiveFlow />

      {/* Products Section */}
      <section id="produkter" className="relative bg-white overflow-hidden border-y border-slate-100">
        <div className="flex flex-col lg:flex-row min-h-[600px]">
          {/* Left side: Image filling the entire left side */}
          <div className="w-full lg:w-1/2 relative min-h-[450px] lg:min-h-auto bg-white">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mockup.png" 
              alt="Medimi Smart" 
              className="absolute inset-0 w-full h-full object-contain p-6 sm:p-10 lg:p-16" 
            />
          </div>
          {/* Right side: Content */}
          <div className="w-full lg:w-1/2 flex items-center py-20 lg:py-32 px-6 sm:px-12 lg:px-16 xl:px-24">
            <RevealSection className="max-w-xl">
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                FlexSuite
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                En samlet produktserie til alle behov – via FlexSuite
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                FlexSuite er vores fælles administrationssystem, der dækker alle aspekter af digital medicinhåndtering i de danske kommuner. Gennem tre specialiserede løsninger sikrer vi den rette støtte til den enkelte borger – fra den fuldautomatiske medicinrobot til den enkle påmindelsesapp.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'TIM – Intelligent medicinrobot til hjemmet',
                  'FlexSmart App – Enkel påmindelsesapp med FMK-integration',
                  'Mobili® – Mobil dispenserløsning til aktive borgere'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <CheckCircle2 size={20} className="text-brand shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4">
                <Link 
                  to="/medimi-smart" 
                  className="inline-flex items-center gap-2 bg-[#2c3223] hover:bg-[#3d4531] text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                >
                  Gå til vores produkt oversigt
                  <i className="fa-light fa-arrow-right transition-transform duration-300 group-hover:translate-x-1"></i>
                </Link>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* 3D & AR Viewer Section */}
      <Tim3DViewer />

      {/* Contact Section */}
      <section id="kontakt" className="bg-slate-100 py-24 text-slate-900 overflow-hidden relative">
        <RevealSection className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-8">Kontakt os for en uforpligtende dialog</h2>
              <p className="text-slate-600 mb-12 max-w-md">
                Er du interesseret i at høre mere om vores løsninger eller ønsker du en demonstration? Udfyld formularen, så vender vi tilbage hurtigst muligt.
              </p>
              <div className="space-y-8">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex gap-5 items-start">
                    <div className="bg-white p-3 rounded-md shadow-sm border border-slate-200">
                      <info.icon className="text-[#2c3223]" size={24} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{info.name}</div>
                      {info.name === 'Email' ? (
                        <a href={`mailto:${info.value}`} className="text-lg font-medium text-slate-900 hover:text-brand-soft transition-colors underline decoration-brand-soft/30 underline-offset-4">
                          {info.value}
                        </a>
                      ) : info.name === 'Telefon' ? (
                        <a href={`tel:${info.value.replace(/\s+/g, '')}`} className="text-lg font-medium text-slate-900 hover:text-brand-soft transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <div className="text-lg font-medium text-slate-900">{info.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-xl border border-slate-200">
              {formStatus === 'success' ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <div className="bg-[#2c3223] p-4 rounded-full mb-6">
                    <CheckCircle2 size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Tak for din besked!</h3>
                  <p className="text-slate-600">Vi vender tilbage til dig inden for 24 timer.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="text" name="website_url" className="sr-only absolute -top-999 left-0" tabIndex="-1" autoComplete="off" />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Navn</label>
                      <input required name="name" type="text" placeholder="Dit navn" className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">E-mail</label>
                      <input required name="email" type="email" placeholder="din@email.dk" className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Emne</label>
                    <input name="subject" type="text" placeholder="Hvad drejer det sig om?" className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#2c3223] ml-1">Besked</label>
                    <textarea required name="message" rows="4" placeholder="Skriv din besked her..." className="w-full bg-slate-50 border-b border-slate-200 py-3 px-4 rounded-t-md focus:outline-none focus:border-[#2c3223] transition-colors resize-none"></textarea>
                  </div>
                  <button type="submit" className="w-full bg-[#2c3223] text-white hover:bg-[#1e2318] font-bold uppercase tracking-widest py-5 rounded-full transition-all flex items-center justify-center gap-3">
                    Send Besked <Send size={18} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </RevealSection>
      </section>

    </>
  );
};

const FolderPage = () => {
  useDocumentMetadata({
    title: 'Brochurer & Informationsmateriale | MedicCare',
    description: 'Download og læs vores foldere og brochurer om Medimi®Smart, TIM, FlexSmart App og Mobili®.'
  });

  const [activeBrochure, setActiveBrochure] = useState('tim');
  const [flexPage, setFlexPage] = useState(1);
  const [isFlexFullscreen, setIsFlexFullscreen] = useState(false);
  const [flexZoom, setFlexZoom] = useState(1);
  const [flexTouchStart, setFlexTouchStart] = useState(null);
  const [flexTouchEnd, setFlexTouchEnd] = useState(null);

  const [mobiliPage, setMobiliPage] = useState(1);
  const [isMobiliFullscreen, setIsMobiliFullscreen] = useState(false);
  const [mobiliZoom, setMobiliZoom] = useState(1);
  const [mobiliTouchStart, setMobiliTouchStart] = useState(null);
  const [mobiliTouchEnd, setMobiliTouchEnd] = useState(null);

  const flexPages = [
    { num: 1, title: "Forside", url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/sl1.png" },
    { num: 2, title: "Bagside", url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/sl2.png" }
  ];

  const mobiliPages = [
    { num: 1, title: "Mobili®", url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili.png" },
    { num: 2, title: "Side 1", url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili2.png" },
    { num: 3, title: "Side 2", url: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili3.png" }
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFlexFullscreen) return;
      if (e.key === 'ArrowRight') {
        setFlexPage((prev) => Math.min(prev + 1, flexPages.length));
      } else if (e.key === 'ArrowLeft') {
        setFlexPage((prev) => Math.max(prev - 1, 1));
      } else if (e.key === 'Escape') {
        setIsFlexFullscreen(false);
        setFlexZoom(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFlexFullscreen, flexPages.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isMobiliFullscreen) return;
      if (e.key === 'ArrowRight') {
        setMobiliPage((prev) => Math.min(prev + 1, mobiliPages.length));
      } else if (e.key === 'ArrowLeft') {
        setMobiliPage((prev) => Math.max(prev - 1, 1));
      } else if (e.key === 'Escape') {
        setIsMobiliFullscreen(false);
        setMobiliZoom(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobiliFullscreen, mobiliPages.length]);

  const handleFlexTouchStart = (e) => {
    setFlexTouchStart(e.targetTouches[0].clientX);
  };

  const handleFlexTouchMove = (e) => {
    setFlexTouchEnd(e.targetTouches[0].clientX);
  };

  const handleFlexTouchEnd = () => {
    if (!flexTouchStart || !flexTouchEnd) return;
    const distance = flexTouchStart - flexTouchEnd;
    const isSwipe = Math.abs(distance) > 50;
    if (isSwipe) {
      if (distance > 0) {
        setFlexPage((prev) => Math.min(prev + 1, flexPages.length));
      } else {
        setFlexPage((prev) => Math.max(prev - 1, 1));
      }
    }
    setFlexTouchStart(null);
    setFlexTouchEnd(null);
  };

  const handleMobiliTouchStart = (e) => {
    setMobiliTouchStart(e.targetTouches[0].clientX);
  };

  const handleMobiliTouchMove = (e) => {
    setMobiliTouchEnd(e.targetTouches[0].clientX);
  };

  const handleMobiliTouchEnd = () => {
    if (!mobiliTouchStart || !mobiliTouchEnd) return;
    const distance = mobiliTouchStart - mobiliTouchEnd;
    const isSwipe = Math.abs(distance) > 50;
    if (isSwipe) {
      if (distance > 0) {
        setMobiliPage((prev) => Math.min(prev + 1, mobiliPages.length));
      } else {
        setMobiliPage((prev) => Math.max(prev - 1, 1));
      }
    }
    setMobiliTouchStart(null);
    setMobiliTouchEnd(null);
  };

  const brochures = {
    tim: {
      title: "Medimi®Smart (TIM)",
      tagline: "Totalløsning til Intelligent Medicinhåndtering",
      description: "Den officielle A5-folder for TIM-medicinrobotten. Læs om, hvordan robotten sikrer korrekt dosering, integrerer med kommunens EOJ-systemer og skaber en tryg hverdag for borgeren.",
      coverImage: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/s1.png",
      pdfUrl: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/a5-folder-medimismart-til-hjemmesiden-og-kunne-bladres-i.pdf",
      pdfName: "A5 Folder MedimiSmart.pdf"
    },
    flex: {
      title: "FlexSmart App",
      tagline: "Enkel og rettidig påmindelsesapp",
      description: "Læs om påmindelsesfunktionen, integrationen til FMK/LMK samt de mange spændende tilkøbsoptioner som videosamtaler og sundhedssupport.",
      coverImage: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/chatgpt-image-6-jul-2026-13-03-57.png",
      pdfUrl: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsmartapp-folder.pdf",
      pdfName: "FlexSmartApp - Folder.pdf"
    },
    mobili: {
      title: "Mobili®",
      tagline: "Den nye 'edoset' medicinhåndteringsløsning",
      description: "Præsentation af Mobili®. Læs om den smarte kassette med 28 rum, apotekspakning, og hvordan løsningen er skræddersyet til mobile borgere med et stabilt medicinskema.",
      coverImage: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili.png",
      pdfUrl: null,
      pdfName: ""
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pt-24">
      {/* Brochure Library Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-semibold uppercase tracking-wider bg-[#c7e4e7]/60 text-[#003f4c] rounded-full border border-[#00b7ad]/20">
              <i className="fa-solid fa-book-open"></i> Informationsmateriale
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
              Digitale Informationsfoldere
            </h1>
            <p className="text-slate-600 text-lg">
              Her kan du læse og downloade det officielle informationsmateriale for vores tre kerneprodukter. Det er det samme materiale, som vi udleverer på møder og messer.
            </p>
          </div>

          {/* Brochure Tabs */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
            {Object.entries(brochures).map(([key, brochure]) => (
              <button
                key={key}
                onClick={() => setActiveBrochure(key)}
                className={`px-5 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 shadow-sm cursor-pointer ${
                  activeBrochure === key
                    ? 'bg-[#003f4c] text-white ring-2 ring-[#00b7ad]/30'
                    : 'bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <i className={`fa-solid ${
                  key === 'tim' ? 'fa-robot' : key === 'flex' ? 'fa-mobile-screen' : 'fa-box-archive'
                }`}></i>
                {brochure.title}
              </button>
            ))}
          </div>

          {/* Brochure Display Area */}
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 md:p-10 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-10">
              <div className="lg:col-span-8">
                <span className="text-xs font-bold uppercase tracking-wider text-[#00b7ad] block mb-2">
                  {brochures[activeBrochure].tagline}
                </span>
                <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                  {activeBrochure === 'mobili' ? brochures[activeBrochure].title : `${brochures[activeBrochure].title} Brochure`}
                </h3>
                <p className="text-slate-600 leading-relaxed text-base md:text-lg mb-6">
                  {brochures[activeBrochure].description}
                </p>
                <div className="flex flex-wrap gap-4">
                  {brochures[activeBrochure].pdfUrl && (
                    <a
                      href={brochures[activeBrochure].pdfUrl}
                      download
                      className="inline-flex items-center gap-2 px-5 py-3 bg-[#003f4c] hover:bg-[#002e38] text-white font-bold rounded-xl text-sm transition-colors shadow-md"
                    >
                      <i className="fa-solid fa-download"></i> Download {brochures[activeBrochure].pdfName}
                    </a>
                  )}
                  {activeBrochure === 'mobili' && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-3 bg-teal-50 text-teal-800 border border-teal-200 rounded-xl text-xs font-semibold">
                      <i className="fa-solid fa-circle-info"></i> Se billeder og information nedenfor.
                    </span>
                  )}
                </div>
              </div>
              <div className="lg:col-span-4 flex justify-center">
                <div className="relative w-48 md:w-56 aspect-[1.414/1] bg-slate-100 rounded-xl overflow-hidden shadow-lg border border-slate-200 group">
                  <img
                    src={brochures[activeBrochure].coverImage}
                    alt={`${brochures[activeBrochure].title} Cover`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-bold text-xs gap-1.5">
                    <i className="fa-solid fa-magnifying-glass-plus text-lg"></i> Vis Folder
                  </div>
                </div>
              </div>
            </div>

            {/* Render Flipbook for TIM, or beautiful interactive guide for others */}
            {activeBrochure === 'tim' ? (
              <div className="border-t border-slate-100 pt-8">
                <FolderFlipbook />
              </div>
            ) : activeBrochure === 'flex' ? (
              <div className="border-t border-slate-100 pt-8 flex flex-col gap-10">
                {/* Interactive Image Viewer for FlexSmart App */}
                <div className="w-full flex flex-col items-center">
                  {/* Top Bar Controls */}
                  <div className="w-full max-w-5xl flex items-center justify-between mb-4 px-2">
                    <div className="flex items-center gap-2 text-slate-700 font-bold text-sm">
                      <i className="fa-solid fa-book-open text-[#00b7ad]"></i>
                      <span>FlexSmart App Brochure — Side {flexPage} af {flexPages.length}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setIsFlexFullscreen(true);
                          setFlexZoom(1);
                        }}
                        className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                        title="Fuld skærm"
                      >
                        <i className="fa-solid fa-expand"></i> Fuld skærm
                      </button>
                      <a
                        href={brochures.flex.pdfUrl}
                        download
                        className="px-3.5 py-2 bg-[#003f4c] hover:bg-[#002e38] text-white font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5"
                      >
                        <i className="fa-solid fa-download"></i> Download {brochures.flex.pdfName}
                      </a>
                    </div>
                  </div>

                  {/* Main Image Container */}
                  <div className="relative w-full max-w-5xl flex items-center justify-center select-none">
                    {/* Left Navigation Arrow */}
                    <button
                      onClick={() => setFlexPage((prev) => Math.max(prev - 1, 1))}
                      disabled={flexPage === 1}
                      className={`absolute left-4 z-40 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-lg border border-slate-200/50 flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                        flexPage === 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      }`}
                      aria-label="Forrige side"
                    >
                      <i className="fa-solid fa-chevron-left text-lg"></i>
                    </button>

                    {/* Right Navigation Arrow */}
                    <button
                      onClick={() => setFlexPage((prev) => Math.min(prev + 1, flexPages.length))}
                      disabled={flexPage === flexPages.length}
                      className={`absolute right-4 z-40 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-lg border border-slate-200/50 flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                        flexPage === flexPages.length ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      }`}
                      aria-label="Næste side"
                    >
                      <i className="fa-solid fa-chevron-right text-lg"></i>
                    </button>

                    {/* Image Display */}
                    <div 
                      className="w-full aspect-[1.414/1] bg-slate-50 rounded-2xl border border-slate-200/60 shadow-md overflow-hidden flex items-center justify-center relative group cursor-pointer"
                      onTouchStart={handleFlexTouchStart}
                      onTouchMove={handleFlexTouchMove}
                      onTouchEnd={handleFlexTouchEnd}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        if (x < rect.width / 3) {
                          setFlexPage((prev) => Math.max(prev - 1, 1));
                        } else if (x > (rect.width * 2) / 3) {
                          setFlexPage((prev) => Math.min(prev + 1, flexPages.length));
                        } else {
                          setIsFlexFullscreen(true);
                          setFlexZoom(1);
                        }
                      }}
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${flexPages[flexPage - 1].url.replace('https://assets.auxodev.dk/', '')}`}
                        alt={`FlexSmart App Brochure - Side ${flexPage}`}
                        className="w-full h-full object-contain"
                        draggable="false"
                      />
                      <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        <i className="fa-solid fa-magnifying-glass-plus"></i> Klik for fuld skærm
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full max-w-5xl mt-4 px-2">
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#00b7ad] transition-all duration-300"
                        style={{ width: `${(flexPage / flexPages.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Thumbnail Strip */}
                  <div className="flex justify-center gap-3 mt-6">
                    {flexPages.map((page) => (
                      <button
                        key={page.num}
                        onClick={() => setFlexPage(page.num)}
                        className={`relative w-24 aspect-[1.414/1] rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                          flexPage === page.num 
                            ? 'border-[#00b7ad] shadow-md scale-105' 
                            : 'border-slate-200 hover:border-slate-400 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=80,format=auto/${page.url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`Miniature side ${page.num}`}
                          className="w-full h-full object-cover"
                          draggable="false"
                        />
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">
                          {page.num}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fullscreen Overlay Modal */}
                {isFlexFullscreen && (
                  <div className="fixed inset-0 bg-slate-950/95 z-50 flex flex-col justify-between p-4 md:p-6 select-none animate-fade-in">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between text-white border-b border-white/10 pb-4">
                      <div className="flex items-center gap-2">
                        <i className="fa-solid fa-book-open text-[#00b7ad]"></i>
                        <span className="font-bold text-sm md:text-base">FlexSmart App Brochure — Side {flexPage} af {flexPages.length}</span>
                      </div>
                      
                      {/* Zoom & Close Controls */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center bg-white/10 rounded-xl p-1 gap-1">
                          <button
                            onClick={() => setFlexZoom((z) => Math.max(z - 0.25, 1))}
                            disabled={flexZoom <= 1}
                            className="p-2 hover:bg-white/10 rounded-lg text-white disabled:opacity-40 transition-colors cursor-pointer"
                            title="Zoom ud"
                          >
                            <i className="fa-solid fa-magnifying-glass-minus"></i>
                          </button>
                          <span className="text-xs font-bold px-2 min-w-[45px] text-center">
                            {Math.round(flexZoom * 100)}%
                          </span>
                          <button
                            onClick={() => setFlexZoom((z) => Math.min(z + 0.25, 3))}
                            disabled={flexZoom >= 3}
                            className="p-2 hover:bg-white/10 rounded-lg text-white disabled:opacity-40 transition-colors cursor-pointer"
                            title="Zoom ind"
                          >
                            <i className="fa-solid fa-magnifying-glass-plus"></i>
                          </button>
                          {flexZoom > 1 && (
                            <button
                              onClick={() => setFlexZoom(1)}
                              className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors cursor-pointer"
                              title="Nulstil zoom"
                            >
                              <i className="fa-solid fa-rotate-left"></i>
                            </button>
                          )}
                        </div>

                        <button
                          onClick={() => {
                            setIsFlexFullscreen(false);
                            setFlexZoom(1);
                          }}
                          className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer"
                          aria-label="Luk fuld skærm"
                        >
                          <i className="fa-solid fa-xmark text-lg"></i>
                        </button>
                      </div>
                    </div>

                    {/* Modal Image Display */}
                    <div className="relative flex-1 flex items-center justify-center overflow-auto py-4">
                      {/* Left Navigation Arrow */}
                      <button
                        onClick={() => setFlexPage((prev) => Math.max(prev - 1, 1))}
                        disabled={flexPage === 1}
                        className={`absolute left-4 z-40 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/10 flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                          flexPage === 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                      >
                        <i className="fa-solid fa-chevron-left text-xl"></i>
                      </button>

                      {/* Right Navigation Arrow */}
                      <button
                        onClick={() => setFlexPage((prev) => Math.min(prev + 1, flexPages.length))}
                        disabled={flexPage === flexPages.length}
                        className={`absolute right-4 z-40 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/10 flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                          flexPage === flexPages.length ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                      >
                        <i className="fa-solid fa-chevron-right text-xl"></i>
                      </button>

                      {/* Fullscreen Image */}
                      <div 
                        className="max-w-7xl w-full h-full flex items-center justify-center p-2"
                        onTouchStart={handleFlexTouchStart}
                        onTouchMove={handleFlexTouchMove}
                        onTouchEnd={handleFlexTouchEnd}
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=95,format=auto/${flexPages[flexPage - 1].url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`FlexSmart App Brochure - Side ${flexPage}`}
                          className="max-w-full max-h-full object-contain select-none"
                          style={{ 
                            transform: `scale(${flexZoom})`, 
                            transition: 'transform 0.2s ease-out' 
                          }}
                          draggable="false"
                        />
                      </div>
                    </div>

                    {/* Modal Thumbnail Strip */}
                    <div className="flex justify-center gap-3 border-t border-white/10 pt-4">
                      {flexPages.map((page) => (
                        <button
                          key={page.num}
                          onClick={() => setFlexPage(page.num)}
                          className={`relative w-20 aspect-[1.414/1] rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            flexPage === page.num 
                              ? 'border-[#00b7ad] scale-105' 
                              : 'border-white/10 hover:border-white/30 opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=160,quality=80,format=auto/${page.url.replace('https://assets.auxodev.dk/', '')}`}
                            alt={`Miniature side ${page.num}`}
                            className="w-full h-full object-cover"
                            draggable="false"
                          />
                          <div className="absolute inset-0 bg-black/20"></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="max-w-4xl mx-auto bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200/60 w-full">
                  <h4 className="font-bold text-slate-900 text-lg mb-6 flex items-center gap-2">
                    <i className="fa-solid fa-list-check text-[#00b7ad]"></i> Hvad indeholder denne folder?
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Kernefunktioner</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Påmindelser, kvitteringer og direkte alarmhåndtering.</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">FlexSuite Platform</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Deler samme stærke platform som TIM-medicinrobotten.</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Integrationer</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Henter automatisk ændringer fra læge og kommune via FMK/LMK.</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Tilkøbsoptioner</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Sundhedssupport (06-23), videosamtaler, pilleskanning og 3. parts alarm.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-t border-slate-100 pt-10 flex flex-col gap-10">
                {/* Interactive Image Viewer for Mobili */}
                <div className="w-full flex flex-col items-center">
                  {/* Top Bar Controls */}
                  <div className="w-full max-w-5xl flex items-center justify-between mb-4 px-2">
                    <div className="flex items-center gap-2 text-slate-700 font-bold text-sm">
                      <i className="fa-solid fa-book-open text-[#00b7ad]"></i>
                      <span>Mobili® Brochure — Side {mobiliPage} af {mobiliPages.length}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setIsMobiliFullscreen(true);
                          setMobiliZoom(1);
                        }}
                        className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                        title="Fuld skærm"
                      >
                        <i className="fa-solid fa-expand"></i> Fuld skærm
                      </button>
                      {brochures.mobili.pdfUrl && (
                        <a
                          href={brochures.mobili.pdfUrl}
                          download
                          className="px-3.5 py-2 bg-[#003f4c] hover:bg-[#002e38] text-white font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5"
                        >
                          <i className="fa-solid fa-download"></i> Download PDF
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Main Image Container */}
                  <div className="relative w-full max-w-5xl flex items-center justify-center select-none">
                    {/* Left Navigation Arrow */}
                    <button
                      onClick={() => setMobiliPage((prev) => Math.max(prev - 1, 1))}
                      disabled={mobiliPage === 1}
                      className={`absolute left-4 z-40 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-lg border border-slate-200/50 flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                        mobiliPage === 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      }`}
                      aria-label="Forrige side"
                    >
                      <i className="fa-solid fa-chevron-left text-lg"></i>
                    </button>

                    {/* Right Navigation Arrow */}
                    <button
                      onClick={() => setMobiliPage((prev) => Math.min(prev + 1, mobiliPages.length))}
                      disabled={mobiliPage === mobiliPages.length}
                      className={`absolute right-4 z-40 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-lg border border-slate-200/50 flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                        mobiliPage === mobiliPages.length ? 'opacity-0 pointer-events-none' : 'opacity-100'
                      }`}
                      aria-label="Næste side"
                    >
                      <i className="fa-solid fa-chevron-right text-lg"></i>
                    </button>

                    {/* Image Display */}
                    <div 
                      className="w-full aspect-[1.414/1] bg-slate-50 rounded-2xl border border-slate-200/60 shadow-md overflow-hidden flex items-center justify-center relative group cursor-pointer"
                      onTouchStart={handleMobiliTouchStart}
                      onTouchMove={handleMobiliTouchMove}
                      onTouchEnd={handleMobiliTouchEnd}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        if (x < rect.width / 3) {
                          setMobiliPage((prev) => Math.max(prev - 1, 1));
                        } else if (x > (rect.width * 2) / 3) {
                          setMobiliPage((prev) => Math.min(prev + 1, mobiliPages.length));
                        } else {
                          setIsMobiliFullscreen(true);
                          setMobiliZoom(1);
                        }
                      }}
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${mobiliPages[mobiliPage - 1].url.replace('https://assets.auxodev.dk/', '')}`}
                        alt={`Mobili® Brochure - Side ${mobiliPage}`}
                        className="w-full h-full object-contain"
                        draggable="false"
                      />
                      <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        <i className="fa-solid fa-magnifying-glass-plus"></i> Klik for fuld skærm
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full max-w-5xl mt-4 px-2">
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#00b7ad] transition-all duration-300"
                        style={{ width: `${(mobiliPage / mobiliPages.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Thumbnail Strip */}
                  <div className="flex justify-center gap-3 mt-6">
                    {mobiliPages.map((page) => (
                      <button
                        key={page.num}
                        onClick={() => setMobiliPage(page.num)}
                        className={`relative w-24 aspect-[1.414/1] rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                          mobiliPage === page.num 
                            ? 'border-[#00b7ad] shadow-md scale-105' 
                            : 'border-slate-200 hover:border-slate-400 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=80,format=auto/${page.url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`Miniature side ${page.num}`}
                          className="w-full h-full object-cover"
                          draggable="false"
                        />
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">
                          {page.num}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fullscreen Overlay Modal */}
                {isMobiliFullscreen && (
                  <div className="fixed inset-0 bg-slate-950/95 z-50 flex flex-col justify-between p-4 md:p-6 select-none animate-fade-in">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between text-white border-b border-white/10 pb-4">
                      <div className="flex items-center gap-2">
                        <i className="fa-solid fa-book-open text-[#00b7ad]"></i>
                        <span className="font-bold text-sm md:text-base">Mobili® Brochure — Side {mobiliPage} af {mobiliPages.length}</span>
                      </div>
                      
                      {/* Zoom & Close Controls */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center bg-white/10 rounded-xl p-1 gap-1">
                          <button
                            onClick={() => setMobiliZoom((z) => Math.max(z - 0.25, 1))}
                            disabled={mobiliZoom <= 1}
                            className="p-2 hover:bg-white/10 rounded-lg text-white disabled:opacity-40 transition-colors cursor-pointer"
                            title="Zoom ud"
                          >
                            <i className="fa-solid fa-magnifying-glass-minus"></i>
                          </button>
                          <span className="text-xs font-bold px-2 min-w-[45px] text-center">
                            {Math.round(mobiliZoom * 100)}%
                          </span>
                          <button
                            onClick={() => setMobiliZoom((z) => Math.min(z + 0.25, 3))}
                            disabled={mobiliZoom >= 3}
                            className="p-2 hover:bg-white/10 rounded-lg text-white disabled:opacity-40 transition-colors cursor-pointer"
                            title="Zoom ind"
                          >
                            <i className="fa-solid fa-magnifying-glass-plus"></i>
                          </button>
                          {mobiliZoom > 1 && (
                            <button
                              onClick={() => setMobiliZoom(1)}
                              className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors cursor-pointer"
                              title="Nulstil zoom"
                            >
                              <i className="fa-solid fa-rotate-left"></i>
                            </button>
                          )}
                        </div>

                        <button
                          onClick={() => {
                            setIsMobiliFullscreen(false);
                            setMobiliZoom(1);
                          }}
                          className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
                          title="Luk fuld skærm"
                        >
                          <i className="fa-solid fa-xmark text-lg"></i>
                        </button>
                      </div>
                    </div>

                    {/* Modal Main Content Area */}
                    <div className="flex-1 flex items-center justify-center relative overflow-auto p-4">
                      {/* Left Navigation Arrow (Fullscreen) */}
                      <button
                        onClick={() => setMobiliPage((prev) => Math.max(prev - 1, 1))}
                        disabled={mobiliPage === 1}
                        className={`absolute left-4 z-40 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                          mobiliPage === 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                        aria-label="Forrige side"
                      >
                        <i className="fa-solid fa-chevron-left text-xl"></i>
                      </button>

                      {/* Right Navigation Arrow (Fullscreen) */}
                      <button
                        onClick={() => setMobiliPage((prev) => Math.min(prev + 1, mobiliPages.length))}
                        disabled={mobiliPage === mobiliPages.length}
                        className={`absolute right-4 z-40 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all hover:scale-105 cursor-pointer ${
                          mobiliPage === mobiliPages.length ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                        aria-label="Næste side"
                      >
                        <i className="fa-solid fa-chevron-right text-xl"></i>
                      </button>

                      {/* Zoomable Image Wrapper */}
                      <div 
                        className="relative max-w-full max-h-[75vh] aspect-[1.414/1] transition-transform duration-200 ease-out"
                        style={{ transform: `scale(${mobiliZoom})` }}
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=95,format=auto/${mobiliPages[mobiliPage - 1].url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`Mobili® Brochure - Side ${mobiliPage}`}
                          className="w-full h-full object-contain rounded-lg shadow-2xl"
                          draggable="false"
                        />
                      </div>
                    </div>

                    {/* Modal Thumbnail Strip */}
                    <div className="flex justify-center gap-3 border-t border-white/10 pt-4">
                      {mobiliPages.map((page) => (
                        <button
                          key={page.num}
                          onClick={() => setMobiliPage(page.num)}
                          className={`relative w-20 aspect-[1.414/1] rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            mobiliPage === page.num 
                              ? 'border-[#00b7ad] scale-105' 
                              : 'border-white/10 hover:border-white/30 opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=160,quality=80,format=auto/${page.url.replace('https://assets.auxodev.dk/', '')}`}
                            alt={`Miniature side ${page.num}`}
                            className="w-full h-full object-cover"
                            draggable="false"
                          />
                          <div className="absolute inset-0 bg-black/20"></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="max-w-4xl mx-auto bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200/60 w-full">
                  <h4 className="font-bold text-slate-900 text-lg mb-6 flex items-center gap-2">
                    <i className="fa-solid fa-list-check text-[#00b7ad]"></i> Nøglefunktioner for Mobili®
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Målgruppe</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Særligt egnet til mobile borgere med et stabilt medicinskema.</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Kassettekapacitet</h5>
                          <p className="text-xs text-slate-600 mt-0.5">28 rum pr. kassette (f.eks. 14 dages rute med 2-4 daglige doseringer).</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Pakkemaskine-funktion</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Intelligent IT-system, der guider personalet i fejlfri påfyldning.</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <i className="fa-solid fa-circle-check text-[#00b7ad] mt-1 shrink-0"></i>
                        <div>
                          <h5 className="font-bold text-slate-800 text-sm">Apotekspakning</h5>
                          <p className="text-xs text-slate-600 mt-0.5">Mulighed for fuld pakning og levering direkte i maskinen af apoteket.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const VideoPage = () => {
  useDocumentMetadata({
    title: 'Videoer & Demonstrationer | Se Medimi®Smart i aktion | MedicCare',
    description: 'Se vores instruktionsvideoer og præsentationer af Medimi®Smart, TIM og hvordan vores intelligente medicindispensere gør en forskel i hverdagen.'
  });

  return (
    <>
      <div className="bg-[#2c3223] py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Udforsk produktspecifikke videoer</h1>
            <p className="text-emerald-100/80 max-w-xl mx-auto text-base md:text-lg">Klik på et af vores produkter nedenfor for at se dedikerede instruktions- og demonstrationsvideoer i vores videobiblioteker.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link 
              to="/medimi-smart/tim#video-section" 
              className="group bg-white/5 hover:bg-white p-8 rounded-2xl border border-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="w-14 h-14 bg-white/10 text-white rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#2c3223] group-hover:text-white transition-all duration-300">
                  <i className="fa-light fa-display-medical text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-[#2c3223] mb-3 transition-colors">Medimi®Smart (TIM)</h3>
                <p className="text-sm text-emerald-100/70 group-hover:text-slate-600 mb-6 leading-relaxed transition-colors">Se videoer om vores intelligente stationære medicindispenser, QR-scanning og den lukkede sløjfe i borgerens eget hjem.</p>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 group-hover:text-[#2c3223] flex items-center gap-2 transition-colors">
                Se TIM videoer <i className="fa-solid fa-arrow-right group-hover:translate-x-1.5 transition-transform"></i>
              </span>
            </Link>

            <Link 
              to="/medimi-smart/flexsmartapp#video-section" 
              className="group bg-white/5 hover:bg-white p-8 rounded-2xl border border-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="w-14 h-14 bg-white/10 text-white rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#2c3223] group-hover:text-white transition-all duration-300">
                  <i className="fa-light fa-mobile-screen-button text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-[#2c3223] mb-3 transition-colors">FlexSmartApp</h3>
                <p className="text-sm text-emerald-100/70 group-hover:text-slate-600 mb-6 leading-relaxed transition-colors">Se videoer om vores cloud-platform, alarmsystemer, eskalering og overblik for plejepersonale og pårørende.</p>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 group-hover:text-[#2c3223] flex items-center gap-2 transition-colors">
                Se app videoer <i className="fa-solid fa-arrow-right group-hover:translate-x-1.5 transition-transform"></i>
              </span>
            </Link>

            <Link 
              to="/medimi-smart/mobili#video-section" 
              className="group bg-white/5 hover:bg-white p-8 rounded-2xl border border-white/10 hover:border-white/20 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="w-14 h-14 bg-white/10 text-white rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#2c3223] group-hover:text-white transition-all duration-300">
                  <i className="fa-light fa-route text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-[#2c3223] mb-3 transition-colors">Mobili®</h3>
                <p className="text-sm text-emerald-100/70 group-hover:text-slate-600 mb-6 leading-relaxed transition-colors">Se videoer om vores bærbare dispenser med GPS-tracking, geofencing og global 4G-forbindelse på farten.</p>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 group-hover:text-[#2c3223] flex items-center gap-2 transition-colors">
                Se Mobili videoer <i className="fa-solid fa-arrow-right group-hover:translate-x-1.5 transition-transform"></i>
              </span>
            </Link>
          </div>
        </div>
      </div>

      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <VideoLibrary productId="tim" defaultVideoSlug="hvad-er-tim" embedded layout="grid" />
          </div>
        </div>
      </div>
    </>
  );
};

const AboutPage = () => {
  useDocumentMetadata({
    title: 'Om MedicCare & CareConsult | Pionerer inden for medicinhåndtering',
    description: 'Læs historien bag MedicCare A/S og CareConsult A/S. Vi har leveret innovative, sikre og dokumenterede velfærdsteknologiske løsninger til de danske kommuner siden 2016.'
  });

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#2c3222] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
            Konsortiet MedicCare består af 2 søsterselskaber
          </h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
            Sikker, intelligent og dokumenteret medicinhåndtering til de danske kommuner. Vi skaber tryghed for borgere og frigiver værdifulde ressourcer i plejesektoren.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-semibold uppercase tracking-wider bg-[#c7e4e7]/60 text-[#003f4c] rounded-full border border-[#00b7ad]/20">
                <i className="fa-solid fa-building-columns"></i> Vores Organisation
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                To stærke søsterselskaber – Én komplet helhedsløsning
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed text-lg">
                <strong>MedicCare A/S</strong> og <strong>CareConsult A/S</strong> arbejder tæt sammen som søsterselskaber for at levere den absolut bedste og mest sikre medicinhåndtering til de danske kommuner.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white text-white flex items-center justify-center shrink-0 shadow-md p-2 border border-slate-100">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png" 
                      alt="TIM" 
                      className="w-8 h-8 object-contain" 
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">MedicCare A/S</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Står for rådgivning, salg, implementering, undervisning og opfølgning. Tillige med lager og logistik samt produktion og refub. af den intelligente hardware og medicinrobotter.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white text-white flex items-center justify-center shrink-0 shadow-md p-2 border border-slate-100">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/support.png" 
                      alt="Sundhedssupport" 
                      className="w-8 h-8 object-contain" 
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">CareConsult A/S</h4>
                    <p className="text-slate-600 leading-relaxed">
                      Varetager software udvikling, 2. line support samt integration mellem kommunernes systemer og vores FlexSuite. CareConsult A/S sikrer, at teknologien bliver en integreret og succesfuld del af kommunens daglige arbejdsgange.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-8">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-100">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mediccare-flow-1.jpg" 
                  alt="MedicCare og CareConsult samarbejde" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-6">
                  <p className="text-white font-medium text-sm">
                    Sikker medicinhåndtering fra lægens ordination til borgerens hånd.
                  </p>
                </div>
              </div>

              {/* Quick Stats / Timeline */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 opacity-10 text-9xl text-white">
                  <i className="fa-solid fa-clock-rotate-left"></i>
                </div>
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <i className="fa-solid fa-history text-[#00b7ad]"></i> Vores Historie & Erfaring
                </h3>
                <div className="space-y-6 relative border-l-2 border-slate-700 pl-6 ml-2">
                  <div className="relative">
                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-[#00b7ad] border-4 border-slate-900"></div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#00b7ad]">2016</span>
                    <h4 className="font-bold text-white text-sm mt-0.5">Pilotprojekt igangsat og gennemført</h4>
                    <p className="text-xs text-slate-300 mt-1">
                      De første spæde skridt tages ved et banebrydende pilotprojekt for at teste teknologien i praksis foregik på Bornholm.
                    </p>
                  </div>
                  <div className="relative">
                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-[#00b7ad] border-4 border-slate-900"></div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#00b7ad]">September 2017</span>
                    <h4 className="font-bold text-white text-sm mt-0.5">Vinder NOPII udbuddet i Nordjylland</h4>
                    <p className="text-xs text-slate-300 mt-1">
                      Konsortiet vinder udbuddet for de ni nordjyske kommuner, hvilket markerer et stort gennembrud.
                    </p>
                  </div>
                  <div className="relative">
                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-[#00b7ad] border-4 border-slate-900"></div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#00b7ad]">2020</span>
                    <h4 className="font-bold text-white text-sm mt-0.5">POC i kommunerne i Midtjylland (flere teknologier)</h4>
                    <p className="text-xs text-slate-300 mt-1">
                      Konsortiet deltager og fortsætter i drift efter POC er afsluttet med kommuner i det midtjyske
                    </p>
                  </div>
                  <div className="relative">
                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-[#00b7ad] border-4 border-slate-900"></div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#00b7ad]">2022/2023</span>
                    <h4 className="font-bold text-white text-sm mt-0.5">Vinder udbud hos nogle af de første kommuner på Sjælland</h4>
                    <p className="text-xs text-slate-300 mt-1">
                      Udbredelsen af vores intelligente medicinhåndteringsløsninger fortsætter til de sjællandske kommuner.
                    </p>
                  </div>
                  <div className="relative">
                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-[#00b7ad] border-4 border-slate-900"></div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#00b7ad]">2025</span>
                    <h4 className="font-bold text-white text-sm mt-0.5">Vinder 6byernes udbud</h4>
                    <p className="text-xs text-slate-300 mt-1">
                      Konsortiet vinder udbud i 6byerne, hvilket udbreder vores intelligente medicinhåndteringsløsninger til landets 6 største kommuner.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const MedimiSmartPage = () => {
  useDocumentMetadata({
    title: 'Produkter | Intelligent Medicinhåndtering | MedicCare',
    description: 'Oplev vores produkter – en samlet produktserie til alle behov inden for digital medicinhåndtering. Fra den intelligente medicinrobot TIM til den enkle APP og mobile Mobili.'
  });

  return (
    <div className="bg-white">
      {/* FlexSuite Central Administration System Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-48 -left-48 w-96 h-96 rounded-full bg-brand-light blur-3xl"></div>
          <div className="absolute -bottom-48 -right-48 w-96 h-96 rounded-full bg-emerald-500 blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Left Side: Visual Integration Diagram */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-3xl p-8 shadow-2xl backdrop-blur-xs">
                <h4 className="text-sm font-bold uppercase tracking-wider text-brand-light mb-8 text-center">
                  FlexSuite Integrationsmodel
                </h4>
                
                <div className="space-y-8 relative">
                  {/* Connection Lines (Visual) */}
                  <div className="absolute left-1/2 top-12 bottom-12 w-0.5 border-l-2 border-dashed border-slate-600 -translate-x-1/2 hidden md:block"></div>
                  
                  {/* Municipal Systems */}
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4 flex items-center gap-4 relative z-10 shadow-lg hover:border-emerald-500/50 transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 overflow-hidden">
                      <img 
                        alt="FMK" 
                        className="w-10 h-10 object-contain" 
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/papir.png" 
                      />
                    </div>
                    <div>
                      <h5 className="font-bold text-sm text-white">Kommunernes Systemer</h5>
                      <p className="text-xs text-slate-400">EOJ-systemer, FMK (Fælles Medicinkort)</p>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-2 relative z-10">
                    <div className="bg-emerald-500/10 text-emerald-400 w-8 h-8 rounded-full flex items-center justify-center border border-emerald-500/20 animate-bounce">
                      <i className="fa-light fa-arrow-down text-xs"></i>
                    </div>
                  </div>

                  {/* FlexSuite - Central Hub */}
                  <div className="bg-gradient-to-br from-[#2c3223] to-[#1e2318] border-2 border-brand-light rounded-2xl p-6 relative z-10 shadow-2xl text-center transform hover:scale-[1.02] transition-all duration-300">
                    <div className="absolute -top-3 right-4 bg-brand-light text-[#1e2318] text-[9px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full">
                      Central Hub
                    </div>
                    <div className="w-20 h-20 rounded-2xl bg-brand-light/10 text-brand-light flex items-center justify-center mx-auto mb-3 p-2">
                      <img
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsuite.png"
                        alt="FlexSuite – bindeleddet"
                        className="w-16 h-16 object-contain"
                      />
                    </div>
                    <h5 className="font-extrabold text-lg text-white mb-1">FlexSuite</h5>
                    <p className="text-xs text-slate-300 max-w-xs mx-auto">
                      Ét fælles administrationssystem med fuld tovejs-integration
                    </p>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-2 relative z-10">
                    <div className="bg-emerald-500/10 text-emerald-400 w-8 h-8 rounded-full flex items-center justify-center border border-emerald-500/20 animate-bounce">
                      <i className="fa-light fa-arrow-down text-xs"></i>
                    </div>
                  </div>

                  {/* Digital Solutions */}
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4 relative z-10 shadow-lg">
                    <h6 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 text-center">
                      Flere og flere digitale løsninger på én aftale
                    </h6>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-slate-900/50 p-2 rounded-lg border border-slate-700/30">
                        <img 
                          src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png" 
                          alt="TIM" 
                          className="w-6 h-6 object-contain mx-auto mb-1" 
                        />
                        <div className="text-[10px] font-bold text-white">TIM</div>
                      </div>
                      <div className="bg-slate-900/50 p-2 rounded-lg border border-slate-700/30">
                        <i className="fa-light fa-mobile-screen text-brand-light mb-1"></i>
                        <div className="text-[10px] font-bold text-white">FlexSmart</div>
                      </div>
                      <div className="bg-slate-900/50 p-2 rounded-lg border border-slate-700/30">
                        <i className="fa-light fa-briefcase-medical text-brand-light mb-1"></i>
                        <div className="text-[10px] font-bold text-white">Mobili®</div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Right Side: Copy & Benefits */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-8">
              <div>
                <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-light text-[#1e2318] rounded-full">
                  FlexSuite administrationssystem
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
                  FlexSuite – Ét fælles administrationssystem
                </h1>
                <p className="text-lg text-slate-300 leading-relaxed">
                  Løsningerne er samlet i ét fælles administrationssystem, kaldet <strong className="text-brand-light">FlexSuite</strong>.
                </p>
              </div>

              <div className="space-y-6 text-slate-300 text-base leading-relaxed">
                <p>
                  FlexSuite kan håndtere flere forskellige digitale løsninger, men det der går igennem dem alle, er at det sker via integration fra kommunernes systemer til og fra vores FlexSuite og derfra videre ud til flere og flere digitale løsninger på en og samme aftale.
                </p>
                <p className="border-l-4 border-brand-light pl-4 italic text-slate-200">
                  Med andre ord så får I kun én kontrakt og med én databehandleraftale adgang til ét it-administrationssystem med flere digitale medicinhåndteringsprodukter, og der kommer løbende nye produkter til.
                </p>
                <p>
                  Det betyder også, at det er ét og samme system, som alle samarbejdspartnere benytter.
                </p>
              </div>

              {/* Key Highlights Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                <div className="bg-slate-800/40 border border-slate-700/30 p-5 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-brand-light/10 text-brand-light flex items-center justify-center mb-3">
                    <i className="fa-light fa-file-signature text-lg"></i>
                  </div>
                  <h4 className="font-bold text-sm text-white mb-1">Én Aftale</h4>
                  <p className="text-xs text-slate-400">Én kontrakt og én databehandleraftale til alle løsninger.</p>
                </div>
                
                <div className="bg-slate-800/40 border border-slate-700/30 p-5 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-brand-light/10 text-brand-light flex items-center justify-center mb-3">
                    <i className="fa-light fa-arrows-repeat text-lg"></i>
                  </div>
                  <h4 className="font-bold text-sm text-white mb-1">Fuld Integration</h4>
                  <p className="text-xs text-slate-400">Tovejs data mellem kommunernes systemer og vores system.</p>
                </div>

                <div className="bg-slate-800/40 border border-slate-700/30 p-5 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-brand-light/10 text-brand-light flex items-center justify-center mb-3">
                    <i className="fa-light fa-users-gear text-lg"></i>
                  </div>
                  <h4 className="font-bold text-sm text-white mb-1">Fælles Platform</h4>
                  <p className="text-xs text-slate-400">Ét samlet system som alle samarbejdspartnere benytter.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-slate-50 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <img src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mediccare-flow-1.jpg" alt="Medimi Smart Helhedsløsning" className="rounded-2xl shadow-2xl" />
          </div>
          <div>
            <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
              FlexSuite
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
              En samlet produktserie til alle behov – via FlexSuite
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              <strong className="text-slate-900">FlexSuite</strong> er det centrale administrationssystem, der styrer det hele, mens vores tre specialiserede produkter sikrer den rette medicin til den rette borger – uanset funktionsniveau og behov for støtte.
            </p>
            <div className="grid grid-cols-1 gap-4">
              {[
                { title: 'FlexSuite', desc: 'Det overordnede IT-system og administrationssystem, der styrer det hele og forbinder alle vores løsninger.', isSystem: true },
                { title: 'TIM (MedimiSmart)', desc: 'Vores pilledispenser til borgere med et komplekst medicinskema i hjemmet.' },
                { title: 'FlexSmart App', desc: 'Enkel påmindelsesapp koblet på FMK/LMK samt FlexSuite.' },
                { title: 'Mobili®', desc: 'Bærbar "edoset"-løsning til mobile borgere på farten.' }
              ].map((item, i) => (
                <div key={i} className={`flex gap-4 p-4 rounded-xl border shadow-xs transition-all ${item.isSystem ? 'bg-brand-soft/20 border-brand-light/30' : 'bg-white border-slate-100'}`}>
                  {item.isSystem ? (
                    <i className="fa-solid fa-server text-[#2c3223] text-xl shrink-0 mt-1" />
                  ) : (
                    <CheckCircle2 size={24} className="text-[#2c3223] shrink-0 mt-0.5" />
                  )}
                  <div>
                    <h4 className="font-bold text-slate-900 flex items-center gap-2">
                      {item.title}
                      {item.isSystem && (
                        <span className="text-[9px] font-extrabold uppercase tracking-wider bg-brand-light text-[#1e2318] px-2.5 py-0.5 rounded-full">
                          Administrationssystem
                        </span>
                      )}
                    </h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Product Lines Hub Section */}
    <section className="py-24 bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
            Vores Produktlinjer
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Tre specialiserede løsninger</h2>
          <p className="text-slate-600">
            Udforsk vores tre produktlinjer, der tilsammen danner en komplet og sikker værdikæde for medicinhåndtering.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* MedimiSmart TIM */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col hover:shadow-md transition-all">
            <div className="h-48 overflow-hidden relative">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medimi_3.jpg" 
                alt="Medimi®Smart (TIM)" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="text-xl font-bold text-slate-900 mb-1">TIM (MedimiSmart)</h3>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-4 text-[#2c3223]">Påminder, dispenserer ”til rette tid og rette mængde”</p>
              <div className="text-slate-600 text-sm leading-relaxed mb-6 flex-1 space-y-3">
                <p>Er vores pilledispenser til borgere, som har brug for mange forskellige typer af medicin og et komplekst medicinskema.</p>
                <ul className="space-y-1 text-xs text-slate-500">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Mange doseringsvinduer og piller
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Hyppige ændringer i medicin og mængder
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Medicinændringer fra lægen slår igennem helt ud til TIM (MedimiSmart)
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Koblet direkte op på FMK/LMK gennem FlexSuite
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Dispenseringstidspunkter aftales med borgeren for individuelt behov
                  </li>
                </ul>
              </div>
              <Link 
                to="/medimi-smart/tim" 
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2c3223] hover:text-brand-light transition-colors mt-auto pt-4 border-t border-slate-100"
              >
                Læs mere om TIM <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          {/* FlexSmartApp */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col hover:shadow-md transition-all">
            <div className="h-48 overflow-hidden relative">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/chatgpt-image-6-jul-2026-13-03-57.png" 
                alt="FlexSmartApp" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="text-xl font-bold text-slate-900 mb-1">FlexSmart App</h3>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-4 text-[#2c3223]">En App, der påminder borgeren om at huske at tage sin medicin</p>
              <div className="text-slate-600 text-sm leading-relaxed mb-6 flex-1 space-y-3">
                <p>Til dem som bare skal have lidt struktur i hverdagen og har et behov for påmindelser.</p>
                <p className="text-xs text-slate-500">
                  En brugervenlig app, som er koblet på FMK/LMK samt FlexSuite, og giver rettidige notifikationer, der hjælper til at få en struktureret hverdag.
                </p>
              </div>
              <Link 
                to="/medimi-smart/flexsmartapp" 
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2c3223] hover:text-brand-light transition-colors mt-auto pt-4 border-t border-slate-100"
              >
                Læs mere om Appen <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          {/* Mobili */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col hover:shadow-md transition-all">
            <div className="h-48 overflow-hidden relative">
              <img 
                src="https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/mobili2_2.png" 
                alt="Mobili®" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="text-xl font-bold text-slate-900 mb-1">Mobili®</h3>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-4 text-[#2c3223]">edoset der påminder, dispenserer ”til rette tid og i rette mængde”</p>
              <div className="text-slate-600 text-sm leading-relaxed mb-6 flex-1 space-y-3">
                <p>Den mobile pilledispenser for dem, som har et medicinbehov og behøver hjælp til at håndtere det.</p>
                <ul className="space-y-1 text-xs text-slate-500">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Så lille at den nemt kan være i håndbagagen på ture og ferie
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Særligt egnet til mobile borgere
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Kan køre på batteri eller almindelig strøm
                  </li>
                </ul>
              </div>
              <Link 
                to="/medimi-smart/mobili" 
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2c3223] hover:text-brand-light transition-colors mt-auto pt-4 border-t border-slate-100"
              >
                Læs mere om Mobili <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Combined Value Chain & Features Section */}
    <section id="services" className="py-24 bg-slate-50 border-t border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
            Helhedsløsningen
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
            Sikkerhed i hjemmet, forbundet med sundhedsvæsenet
          </h2>
          <p className="text-slate-600">
            Vi forbinder læger, apoteker, kommune og borgere i én sammenhængende digital værdikæde, der sikrer borgeren den rette medicin til tiden.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Group 1: Borgerens Tryghed */}
          <div className="space-y-8">
            <div className="border-b border-slate-200 pb-4">
              <div className="flex items-center gap-3 mb-1">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2c3222]/10 text-[#2c3222] text-sm font-bold">1</span>
                <h3 className="text-xl font-bold text-slate-900">Tryghed &amp; Sikkerhed i Hjemmet</h3>
              </div>
              <p className="text-sm text-slate-500 ml-11">Smarte funktioner og påmindelser direkte hos borgeren.</p>
            </div>

            <div className="space-y-6">
              {features.map((feature, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 flex gap-5 border border-slate-100 shadow-xs hover:shadow-md transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-[#2c3222]/10 text-[#2c3222] flex items-center justify-center shrink-0 group-hover:bg-[#2c3222] group-hover:text-white transition-all duration-300">
                    <i className={`${feature.iconClass || 'fa-light fa-bell'} text-xl`} />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-900 mb-1.5">{feature.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Group 2: Den Digitale Værdikæde */}
          <div className="space-y-8">
            <div className="border-b border-slate-200 pb-4">
              <div className="flex items-center gap-3 mb-1">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2c3222]/10 text-[#2c3222] text-sm font-bold">2</span>
                <h3 className="text-xl font-bold text-slate-900">Den Digitale Værdikæde</h3>
              </div>
              <p className="text-sm text-slate-500 ml-11">Integration mellem læger, apoteker og kommunen</p>
            </div>

            <div className="space-y-6">
              {services.map((service, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 flex gap-5 border border-slate-100 shadow-xs hover:shadow-md transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-[#2c3222]/10 text-[#2c3222] flex items-center justify-center shrink-0 group-hover:bg-[#2c3222] transition-all duration-300 p-2">
                    {service.image ? (
                      <img src={service.image} alt={service.title} className="w-full h-full object-contain" />
                    ) : (
                      <i className={`${service.iconClass || 'fa-light fa-circle'} text-xl group-hover:text-white`} />
                    )}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-900 mb-1.5">{service.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Tekniske Specifikationer &amp; Sikkerhed</h2>
          <p className="text-slate-600">FlexSuite og produktserien er udviklet med fokus på ultimativ sikkerhed, compliance og fuld integration.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center mx-auto mb-4 p-2">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsuite.png"
                alt="FlexSuite – bindeleddet"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-xl font-bold text-slate-900 mb-2">FlexSuite og Integration</div>
            <p className="text-sm text-slate-600">Integration mellem kommunernes omsorgssystemer (LMK/FMK) og vores FlexSuite</p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-shield-check text-lg"></i>
            </div>
            <div className="text-xl font-bold text-slate-900 mb-2">Høj Præcision &amp; CE</div>
            <p className="text-sm text-slate-600">Dokumenteret korrekt dosering på over 99,8 %. CE-godkendt og klassificeret som medicinsk udstyr.</p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-truck-ramp-box text-lg"></i>
            </div>
            <div className="text-xl font-bold text-slate-900 mb-2">Fleksibel Påfyldning</div>
            <p className="text-sm text-slate-600">Understøtter både apotekspakkede kassetter og lokal påfyldning via intelligent, guidet IT-system.</p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-key text-lg"></i>
            </div>
            <div className="text-xl font-bold text-slate-900 mb-2">Sikkert Login</div>
            <p className="text-sm text-slate-600">To-faktor login via SMS-kode eller direkte login via FK-adgang/Kombit for sundhedspersonale.</p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-battery-three-quarters text-lg"></i>
            </div>
            <div className="text-xl font-bold text-slate-900 mb-2">Backup &amp; Forbindelse</div>
            <p className="text-sm text-slate-600">Backupbatteri sikrer fuld drift i op til 24 timer. Online opkobling via sikker router til FlexSuite.</p>
          </div>
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-full bg-brand-soft text-[#2c3223] flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-sliders text-lg"></i>
            </div>
            <div className="text-xl font-bold text-slate-900 mb-2">Fleksible Optioner</div>
            <p className="text-sm text-slate-600">Mulighed for behovsmedicin, feriekassetter, vederlagsfri medicin og remote dispenseringshjælp.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
  );
};

const MainApp = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const targetId = hash.substring(1);
      // Wait a tiny bit for the DOM to be fully rendered/ready
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 90;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  const isGammelSide = pathname.startsWith('/gammelside');
  const isELaeringStandalone = pathname === '/e-laering' || pathname === '/e-læring' || pathname === '/e-learning' || pathname.startsWith('/e-laering/') || pathname.startsWith('/e-læring/');

  if (isGammelSide) {
    return (
      <Routes>
        <Route path="/gammelside/*" element={<GammelSideApp />} />
      </Routes>
    );
  }

  if (isELaeringStandalone) {
    return (
      <Routes>
        <Route path="/e-laering" element={<ELaering />} />
        <Route path="/e-læring" element={<ELaering />} />
        <Route path="/e-learning" element={<Navigate to="/e-laering" replace />} />
        <Route path="*" element={<ELaering />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800">
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      
      <main>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/medimi-smart" element={<MedimiSmartPage />} />
          <Route path="/medimi-smart/tim" element={<MedimiSmartTim />} />
          <Route path="/medimi-smart/flexsmartapp" element={<FlexSmartApp />} />
          <Route path="/medimi-smart/mobili" element={<Mobili />} />
          <Route path="/folder" element={<FolderPage />} />
          <Route path="/video" element={<VideoPage />} />
          <Route path="/video/:videoSlug" element={<VideoWatch />} />
          <Route path="/om-os" element={<AboutPage />} />
          <Route path="/instruktioner" element={<Instruktioner />} />
          <Route path="/e-laering" element={<ELaering />} />
          <Route path="/e-læring" element={<ELaering />} />
          <Route path="/e-learning" element={<Navigate to="/e-laering" replace />} />
          <Route path="/privatlivspolitik" element={<PrivacyPolicy />} />

          {/* Legacy 301/Redirects from old domain structure */}
          <Route path="/index.php" element={<Navigate to="/" replace />} />
          <Route path="/medimismart.php" element={<Navigate to="/medimi-smart" replace />} />
          <Route path="/borgerscreening.php" element={<Navigate to="/medimi-smart" replace />} />
          <Route path="/om-mediccare.php" element={<Navigate to="/om-os" replace />} />
          <Route path="/kontakt.php" element={<Navigate to="/#kontakt" replace />} />
          <Route path="/video.php" element={<Navigate to="/video" replace />} />
          <Route path="/folder.php" element={<Navigate to="/folder" replace />} />
          <Route path="/TIM/index.php" element={<Navigate to="/medimi-smart/tim" replace />} />
          <Route path="/Sverige/index.php" element={<Navigate to="/medimi-smart" replace />} />
          <Route path="/apotek/index.php" element={<Navigate to="/medimi-smart" replace />} />
          <Route path="/intern/index.php" element={<Navigate to="/instruktioner" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
};

export default MainApp;
